import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { COLOR_PRIMARY, exchangeRates, rating_marks, REPORT_TYPE_TOOLTIPS } from 'src/utils/const';
import { reportPageStyles as styles } from '../styles';

import CloseIcon from '@mui/icons-material/Close';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { KpiPercentileType, ReportTypes } from '../../../propTypes';
import { beautifyDate, formatCurrency, handleImageInput, roundFloat } from 'src/utils/functions';
import { COLOR_GREY_600, COLOR_PRIMARY_1_100, COLOR_TEXT_4 } from 'src/utils/colors';
import EditButton from 'src/App/Dashboards/Market/Components/Buttons/EditButton';
import { styled, useTheme } from '@mui/material/styles';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { feetToMeterConversion, meterToFeetConversion } from '../../../helper';
import { buildReportFilterUpdatePayload } from '../buildReportFilterUpdatePayload';
import DisabledBlurCard from '../../../UtilityComponents/BlurCard';
import InfoTooltipContent from 'src/App/Dashboards/Market/Components/Tooltips/InfoTooltipContent';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';

const RepotPageInputSection = ({
  reportInputs,
  currentPropertyPriceOrRentPrice,
  handleReport,
  currency,
  isUserReportOwner,
  trackEditOpened,
  trackEditPerformed,
  isOnboardingComplete,
  isUserSubscribed,
  handleLoginOrPricingModalOpen,
  loginCountryCode,
  selectedKpiPercentile,
  setSelectedKpiPercentile,
  selectedCompsCount,
  isCompsKpiPresent,
  handleCompsUpdate,
  compsStatus,
  isEditedKpiAvailable,
  autoOpenFilter = false,
  onFilterClose,
}: {
  reportInputs: any;
  currentPropertyPriceOrRentPrice: number | null;
  handleReport: (
    method: any,
    requestBody: null | any,
    id?: string,
    storeID?: boolean,
    hideLoading?: boolean,
    forcePercentile?: KpiPercentileType
  ) => Promise<{ success: boolean }>;
  currency: string;
  isUserReportOwner: boolean;
  trackEditOpened: (editType: any) => void;
  trackEditPerformed: (editType: any) => void;
  isOnboardingComplete: boolean;
  isUserSubscribed: boolean;
  handleLoginOrPricingModalOpen: (state: 'open' | 'close', forceOpen?: 'payment' | null) => void;
  loginCountryCode: string;
  selectedKpiPercentile: KpiPercentileType;
  setSelectedKpiPercentile: (value: KpiPercentileType) => void;
  selectedCompsCount: number;
  isCompsKpiPresent: boolean;
  handleCompsUpdate: () => void;
  compsStatus: 'fetching' | 'success' | 'failed';
  isEditedKpiAvailable: boolean;
  autoOpenFilter?: boolean;
  onFilterClose?: () => void;
}) => {
  const {
    address_full: addressFull,
    address_short: addressShort,
    bedrooms,
    bathrooms,
    min_nights,
    professionally_managed: professionallyManaged,
    review_count_min: reviewCountMin,
    rating_max: ratingMax,
    rating_min: ratingMin,
    accommodates,
    report_type: reportType,
    amenities,
    created_at: createdAt,
  } = reportInputs;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [reportTypeEditMode, setReportTypeEditMode] = useState<boolean>(false);
  const [filterEditMode, setFilterEditMode] = useState<boolean>(autoOpenFilter);
  const [highlitedFilterMode, setHighlitedFilterMode] = useState<string | null>(null);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState<boolean>(false);

  const [selectedReportType, setSelectedReportType] = useState<ReportTypes>(reportType);

  const [updatedPropertyPriceOrRoomRent, setUpdatedPropertyPriceOrRoomRent] = useState<
    number | null
  >(currentPropertyPriceOrRentPrice);

  const [bedroom, setBedroom] = useState<number>(bedrooms);
  const [bathroom, setBathroom] = useState<number>(bathrooms);
  const [accommodate, setAccommodate] = useState<number>(accommodates);
  const [stayDuration, setStayDuration] = useState<string>(
    min_nights !== null ? min_nights : 'both'
  );
  const [professionallyManagedState, setProfessionallyManagedState] = useState<'both' | 1 | 0>(
    professionallyManaged !== null ? professionallyManaged : 'both'
  );
  const [ratingState, setRatingState] = useState<[number, number]>(
    ratingMin && ratingMax ? [ratingMin, ratingMax] : [3.9, 5]
  );
  const [reviewCountState, setReviewCountState] = useState<number | 'any'>(
    reviewCountMin !== null ? reviewCountMin : 'any'
  );
  const [amenitiesState, setAmenitiesState] = useState<string[]>(amenities ? amenities : []);

  const [reportName, setReportName] = useState<string | null>(
    reportInputs.report_name ? reportInputs.report_name : null
  );
  const currentPropetySizeUnit =
    (localStorage.getItem('property-size-unit') as 'm' | 'ft') ||
    (loginCountryCode === 'US' ? 'ft' : 'm');

  const [propertySizeUnit, setPropertySizeUnit] = useState<'m' | 'ft'>(currentPropetySizeUnit);

  const [currentPropertySize, setCurrentPropertySize] = useState<number | null>(
    reportInputs.report_name
      ? currentPropetySizeUnit === 'm'
        ? reportInputs.property_size_sqm
        : meterToFeetConversion(reportInputs.property_size_sqm)
      : null
  );
  const [buildYear, setBuildYear] = useState<number | null>(
    reportInputs.year_built ? reportInputs.year_built : null
  );
  const [linkToProperty, setLinkToProperty] = useState<string | null>(
    reportInputs.link_to_property ? reportInputs.link_to_property : null
  );
  const [notes, setNotes] = useState<string | null>(reportInputs.notes ? reportInputs.notes : null);
  const [images, setImages] = useState<string | null>(null);

  const [propertyDetailsModalOpen, setPropertyDetailsModalOpen] = useState<boolean>(false);

  const handleCloseReportTypeEditModal = () => {
    setSelectedReportType(reportType);
    setUpdatedPropertyPriceOrRoomRent(currentPropertyPriceOrRentPrice);
    setReportTypeEditMode(false);
  };

  const handleOpenFilterModal = (displayId: string) => {
    if (isOnboardingComplete && !isUserSubscribed) {
      handleLoginOrPricingModalOpen('open', 'payment');
      return;
    }
    setHighlitedFilterMode(displayId);
    setFilterEditMode(true);
    trackEditOpened('input');

    if (
      ['stayDuration', 'professionallyManaged', 'rating', 'reviewCount', 'amenities'].includes(
        displayId
      )
    ) {
      setIsAdvancedFilterOpen(_ => true);
    }
  };

  const handleCloseEditModal = () => {
    setFilterEditMode(false);
    setHighlitedFilterMode(null);
    onFilterClose?.();
  };

  const handleUnitFilterButtonClick = (displayId: string) => {
    if (isOnboardingComplete && !isUserSubscribed) {
      handleLoginOrPricingModalOpen('open', 'payment');
      return;
    }
    setHighlitedFilterMode(displayId);
    setFilterEditMode(true);
    trackEditOpened('input');
  };

  const handleUpdateBathroomAccommodateWithBedroomUpdate = (newBedroomValue: number) => {
    let newBathroomCount: number | null = null;
    let newAccommodateCount: number | null = null;

    switch (newBedroomValue) {
      case 0:
        newBathroomCount = 1;
        newAccommodateCount = 2;
        break;
      case 1:
        newBathroomCount = 1;
        newAccommodateCount = 2;
        break;
      case 2:
        newBathroomCount = 1;
        newAccommodateCount = 4;
        break;
      case 3:
        newBathroomCount = 2;
        newAccommodateCount = 6;
        break;
      case 4:
        newBathroomCount = 3;
        newAccommodateCount = 8;
        break;
      case 5:
        newBathroomCount = 4;
        newAccommodateCount = 10;
        break;
      case 6:
        newBathroomCount = 5;
        newAccommodateCount = 12;
        break;
      default:
        break;
    }

    newBathroomCount && setBathroom(newBathroomCount);
    newAccommodateCount && setAccommodate(newAccommodateCount);
  };

  const getSubFilterText = (isUserReportOwner: boolean) => {
    const subFilters: JSX.Element[] = [];
    if (min_nights) {
      subFilters.push(
        <Typography
          sx={styles.reportInputSectionSubInput}
          onClick={() => isUserReportOwner && handleOpenFilterModal('stayDuration')}
        >{`${min_nights.replace('-', ' ')}`}</Typography>
      );
    }
    if (professionallyManaged) {
      subFilters.push(
        <Typography
          sx={styles.reportInputSectionSubInput}
          onClick={() => isUserReportOwner && handleOpenFilterModal('professionallyManaged')}
        >{`${
          professionallyManaged == 1
            ? 'Professionally Managed'
            : professionallyManaged == 0
            ? 'Not Professionally Managed'
            : ''
        }`}</Typography>
      );
    }
    if (reviewCountMin) {
      subFilters.push(
        <Typography
          sx={styles.reportInputSectionSubInput}
          onClick={() => isUserReportOwner && handleOpenFilterModal('reviewCount')}
        >{`${reviewCountMin}+ reviews`}</Typography>
      );
    }
    if (ratingMax && !(ratingMin == 3.9 && ratingMax == 5)) {
      subFilters.push(
        <Typography
          sx={styles.reportInputSectionSubInput}
          onClick={() => isUserReportOwner && handleOpenFilterModal('rating')}
        >{`${ratingMin} - ${ratingMax} Star`}</Typography>
      );
    }
    if (amenities) {
      subFilters.push(
        <Typography
          sx={styles.reportInputSectionSubInput}
          onClick={() => isUserReportOwner && handleOpenFilterModal('amenities')}
        >
          {amenities.join(', ')}
        </Typography>
      );
    }
    return subFilters;
  };

  const getCollapseHeadingButton = (title, isModalOpen, setModalOpen) => {
    return (
      <Grid
        container
        justifyContent="space-between"
        alignItems={'center'}
        xs={12}
        onClick={() => setModalOpen(prev => !prev)}
        sx={{
          cursor: 'pointer',
          borderRadius: '10px',
          transition: 'all 0.25s ease',
          '&:hover': {
            paddingLeft: '20px',
            paddingRight: '20px',
            background: COLOR_PRIMARY_1_100,
          },
        }}
      >
        <Grid item>
          <Typography variant="body1" sx={{ ...styles.filterHeading, mt: '20px' }}>
            {title}
          </Typography>
        </Grid>
        <Grid item>
          <ArrowForwardIosRoundedIcon
            sx={{
              color: '#8B92A5',
              transition: 'transform 0.25s ease',
              transform: isModalOpen ? 'rotate(0deg)' : 'rotate(90deg)',
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const reportTypeEditModal = () => {
    const isApplyButtonDisabled =
      selectedReportType !== 'manage' &&
      (updatedPropertyPriceOrRoomRent == null || updatedPropertyPriceOrRoomRent == 0);

    return (
      <Modal open={reportTypeEditMode} onClose={handleCloseReportTypeEditModal}>
        <Box sx={styles.filterModalBodyRoot}>
          <IconButton onClick={handleCloseReportTypeEditModal} sx={styles.filterModalCloseIcon}>
            <CloseIcon />
          </IconButton>

          <Grid container xs justifyContent="space-between" sx={{ mt: '10px' }}>
            <Grid item md={12} lg={6}>
              <Typography variant="body1" sx={styles.filterGroupHeadingTextColor}>
                Report Type
              </Typography>
            </Grid>
            <Grid
              container
              item
              sm={12}
              md={6}
              spacing={2}
              justifyContent={'flex-end'}
              sx={{ mb: '15px' }}
            >
              {(['buy', 'rent', 'manage'] as ReportTypes[]).map(curentReportType => (
                <Grid item>
                  <TooltipNew content={REPORT_TYPE_TOOLTIPS[curentReportType]}>
                    <Chip
                      label={curentReportType}
                      variant="outlined"
                      onClick={() => setSelectedReportType(curentReportType)}
                      sx={styles.filterChipStyle(
                        selectedReportType.toLowerCase() === curentReportType.toLowerCase()
                      )}
                    />
                  </TooltipNew>
                </Grid>
              ))}
            </Grid>

            {selectedReportType !== 'manage' &&
              Filter([
                {
                  id: 'reportType',
                  title:
                    selectedReportType.toLowerCase() === 'buy' ? 'Property Price' : 'Monthly Rent',
                  filterComponentTypeObj: {
                    type: 'text',
                    isNumber: true,
                    formatNumber: true,
                    inputPrefix: exchangeRates[currency].symbol,
                  },
                  currentFilterState: updatedPropertyPriceOrRoomRent,
                  setUpdatedFilterObj: setUpdatedPropertyPriceOrRoomRent,
                  dividerAfter: false,
                },
              ])}

            <Divider sx={{ mb: '10px' }} />

            {selectedReportType === 'buy' &&
              getCollapseHeadingButton(
                'Property Details',
                propertyDetailsModalOpen,
                setPropertyDetailsModalOpen
              )}

            <Collapse in={propertyDetailsModalOpen} timeout="auto" unmountOnExit>
              <Grid container xs={12}>
                {selectedReportType === 'buy' &&
                  Filter([
                    {
                      id: 'reportName',
                      title: 'Report Name',
                      filterComponentTypeObj: {
                        type: 'text',
                        isNumber: false,
                        formatNumber: false,
                        maxLength: 250,
                      },
                      currentFilterState: reportName,
                      setUpdatedFilterObj: setReportName,
                      dividerAfter: false,
                    },
                    {
                      id: 'propertySize',
                      title: 'Property Size',
                      filterComponentTypeObj: {
                        type: 'text',
                        isNumber: true,
                        formatNumber: true,
                        maxMinNumberRange: [1, 9999999],
                        endAdornmentComponent: meterFeetComversionButtonGroup,
                      },
                      currentFilterState: currentPropertySize
                        ? roundFloat(currentPropertySize)
                        : null,
                      setUpdatedFilterObj: setCurrentPropertySize,
                      dividerAfter: false,
                    },
                    {
                      id: 'buildYear',
                      title: 'Build Year',
                      filterComponentTypeObj: {
                        type: 'text',
                        isNumber: true,
                        formatNumber: false,
                        maxMinNumberRange: [1, 2024],
                      },
                      currentFilterState: buildYear,
                      setUpdatedFilterObj: setBuildYear,
                      dividerAfter: false,
                    },
                    {
                      id: 'linkToProperty',
                      title: 'Link to Property',
                      filterComponentTypeObj: {
                        type: 'text',
                        isNumber: false,
                        formatNumber: false,
                        maxLength: 1000,
                      },
                      currentFilterState: linkToProperty,
                      setUpdatedFilterObj: setLinkToProperty,
                      dividerAfter: false,
                    },
                    {
                      id: 'notes',
                      title: 'Notes',
                      filterComponentTypeObj: {
                        type: 'text',
                        isNumber: false,
                        formatNumber: false,
                        multiline: true,
                        maxLength: 1000,
                      },
                      currentFilterState: notes,
                      setUpdatedFilterObj: setNotes,
                      dividerAfter: false,
                    },
                    {
                      id: 'images',
                      title: 'Images',
                      filterComponentTypeObj: {
                        type: 'image',
                        maxImageCount: 1,
                        maxSizeInMb: 10,
                      },
                      currentFilterState: images,
                      setUpdatedFilterObj: setImages,
                      dividerAfter: false,
                    },
                  ])}
              </Grid>
            </Collapse>

            <Grid container item xs justifyContent="flex-end" sx={{ mt: '20px' }}>
              <Button
                onClick={handleOnFilterUpdateClick}
                sx={styles.actionSectionButton}
                disabled={isApplyButtonDisabled}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    );
  };

  const FilterCounter = (
    minCount: number,
    maxCount: number,
    specialLabels: { [key: number | string]: number | string },
    currentFilterState: number,
    setUpdatedFilterObj: any,
    sideEffectFunction: ((newValue: number) => void) | null = null,
    step: number = 1
  ) => {
    const handleBedroomCount = (type: 'increase' | 'decrease') => {
      if (type === 'increase') {
        const newValue = Math.min(currentFilterState + step, maxCount);
        setUpdatedFilterObj(newValue);
        sideEffectFunction && sideEffectFunction(newValue);
      }
      if (type === 'decrease') {
        const newValue = Math.max(currentFilterState - step, minCount);
        setUpdatedFilterObj(newValue);
        sideEffectFunction && sideEffectFunction(newValue);
      }
    };

    return (
      <>
        <Grid
          item
          container
          alignItems="center"
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        >
          <Grid
            item
            sx={styles.bedroomCounterButton}
            onClick={() => handleBedroomCount('decrease')}
          >
            <RemoveRoundedIcon />
          </Grid>
          <Typography
            variant="body1"
            sx={{ fontSize: '16px', marginLeft: '10px', marginRight: '10px' }}
          >
            {specialLabels[currentFilterState]
              ? specialLabels[currentFilterState]
              : currentFilterState}
          </Typography>
          <Grid
            item
            sx={styles.bedroomCounterButton}
            onClick={() => handleBedroomCount('increase')}
          >
            <AddRoundedIcon />
          </Grid>
        </Grid>
      </>
    );
  };

  const FilterButtonGroup = (
    buttons: { [key: string]: string },
    currentFilterState: string,
    setUpdatedFilterObj: any
  ) => {
    return (
      <Grid
        container
        item
        // sm={12}
        // md={6}
        spacing={2}
        justifyContent={{
          xs: 'flex-start',
          sm: 'flex-end',
        }}
        // flexWrap={'nowrap'}
      >
        {Object.keys(buttons).map(button => (
          <Grid item>
            <Chip
              label={buttons[button]}
              variant="outlined"
              onClick={() => setUpdatedFilterObj(button)}
              sx={styles.filterChipStyle(button == currentFilterState)}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const FilterSlider = (
    rating: [number, number],
    rating_marks: { value: number; label: string }[],
    setUpdatedFilterObj: any
  ) => {
    const handleRatingChange = (event: any, newValue: number | number[]) => {
      const sortedNewValue = (newValue as [number, number]).sort((a, b) => a - b);
      setUpdatedFilterObj(sortedNewValue as [number, number]);
    };

    return (
      <Grid
        container
        item
        sm={12}
        md={6}
        spacing={2}
        justifyContent={'flex-start'}
        sx={{
          width: '100%',
          [theme.breakpoints.down('sm')]: {
            ml: '10px !important',
          },
        }}
      >
        <Slider
          size="small"
          value={rating}
          min={3.9}
          max={5}
          step={0.1}
          marks={rating_marks}
          draggable={true}
          onChange={handleRatingChange}
          valueLabelDisplay="auto"
          valueLabelFormat={x => (x === 3.9 ? '<4.0' : x)}
          sx={{
            color: COLOR_PRIMARY,
            width: '100%',
          }}
        />
      </Grid>
    );
  };

  const MultipleSelectButtonGroup = (
    buttons: { [key: string]: string },
    currentFilterState: string[],
    setUpdatedFilterObj: any,
    maxSelect: number
  ) => {
    const isMaxAmountOfAmenitiesSelected = currentFilterState.length >= maxSelect;

    return (
      <Grid
        container
        item
        spacing={2}
        justifyContent={{
          xs: 'flex-start',
          sm: 'flex-end',
        }}
        sx={{ maxWidth: 400 }}
      >
        {Object.keys(buttons).map(button => (
          <Grid item xs={6} sm={4}>
            <Chip
              label={buttons[button]}
              variant="outlined"
              onClick={() => {
                if (currentFilterState.includes(button)) {
                  setUpdatedFilterObj(currentFilterState.filter((item: string) => item !== button));
                } else {
                  !isMaxAmountOfAmenitiesSelected &&
                    setUpdatedFilterObj([...currentFilterState, button]);
                }
              }}
              sx={{
                ...styles.filterChipStyle(currentFilterState.includes(button)),
                width: '120px',
                fontSize: '14px',
              }}
              disabled={isMaxAmountOfAmenitiesSelected && !currentFilterState.includes(button)}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const SimpleTextInput = (
    title,
    currentFilterState,
    setUpdatedFilterObj,
    isNumber,
    formatNumber,
    multiline = false,
    inputPrefix: string | undefined = undefined,
    maxMinNumberRange: [number, number] | undefined = undefined,
    endAdornmentComponent: JSX.Element | undefined = undefined,
    maxLength: number | undefined = undefined
  ) => {
    return (
      <Grid container xs={12}>
        <Grid item md={12} lg={6} sx={{ mt: '20px' }} alignSelf={'center'}>
          <Typography variant="body1" sx={styles.filterGroupHeadingTextColor}>
            {title}
          </Typography>
        </Grid>
        <Grid
          container
          item
          sm={12}
          md={6}
          spacing={2}
          justifyContent={'flex-end'}
          sx={{ mt: '0px' }}
        >
          <TextField
            variant="filled"
            label={undefined}
            InputLabelProps={{ shrink: false }}
            multiline={multiline ? true : false}
            fullWidth
            value={
              currentFilterState
                ? formatNumber
                  ? formatCurrency(String(currentFilterState))
                  : currentFilterState
                : ''
            }
            onChange={e => {
              let valueInNumber = isNumber ? Number(e.target.value.replace(/,/gi, '')) : null;
              valueInNumber = valueInNumber
                ? maxMinNumberRange
                  ? Math.max(maxMinNumberRange[0], Math.min(valueInNumber, maxMinNumberRange[1]))
                  : valueInNumber
                : null;
              setUpdatedFilterObj(valueInNumber ? valueInNumber : e.target.value);
            }}
            InputProps={{
              ...(inputPrefix
                ? {
                    startAdornment: (
                      <Typography variant="body1" sx={{ fontSize: '18px', fontWeight: 500 }}>
                        {exchangeRates[currency].symbol}
                      </Typography>
                    ),
                  }
                : {}),
              ...(endAdornmentComponent
                ? {
                    endAdornment: endAdornmentComponent,
                  }
                : {}),
            }}
            inputProps={{
              maxLength: maxLength,
            }}
            sx={{
              my: 2,
              '& .MuiInputBase-input': {
                paddingTop: '16px !important',
                paddingBottom: '16px !important',
                paddingLeft: '10px !important',
              },
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const meterFeetComversionButtonGroup = (
    <Stack spacing={2} sx={{ alignItems: 'center' }}>
      <ToggleButtonGroup size="small" value={propertySizeUnit}>
        <ToggleButton
          value="m"
          key="m2"
          onClick={() => {
            setPropertySizeUnit('m');
            localStorage.setItem('property-size-unit', 'm');
          }}
        >
          <Typography variant="body2">
            m<sup style={{ verticalAlign: 'super', fontSize: '10px' }}>2</sup>
          </Typography>
        </ToggleButton>
        <ToggleButton
          value="ft"
          key="ft2"
          onClick={() => {
            setPropertySizeUnit('ft');
            localStorage.setItem('property-size-unit', 'ft');
          }}
        >
          <Typography variant="body2">
            ft<sup style={{ verticalAlign: 'super', fontSize: '10px' }}>2</sup>
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const ImageInput = (title, images, setImages, maxImageCount = 1, maxSizeInMb: number = 10) => {
    const handleImageUpload = async value => {
      const data = await handleImageInput(value);
      setImages(data);
    };

    return (
      <>
        <Grid container xs={12} sx={{ mt: '10px' }}>
          <Grid item md={12} lg={6} sx={{ mt: '20px' }} alignSelf={'center'}>
            <Typography variant="body1" sx={styles.filterGroupHeadingTextColor}>
              {title}
            </Typography>
          </Grid>
          <Grid
            item
            container
            sm={12}
            md={6}
            spacing={2}
            flexDirection={'row'}
            alignItems={'flex-end'}
            justifyContent={'flex-end'}
          >
            <Grid item>
              {((images && images.length > 0) || reportInputs.property_image_urls) && (
                <img
                  src={images ? images : reportInputs.property_image_urls}
                  style={{ width: '80px', height: '40px', objectFit: 'contain' }}
                />
              )}
            </Grid>
            <Grid item>
              <Button
                fullWidth
                component="label"
                role={undefined}
                variant="outlined"
                tabIndex={-1}
                startIcon={<AddPhotoAlternateIcon />}
              >
                Upload files
                <VisuallyHiddenInput
                  type="file"
                  onChange={event => handleImageUpload(event.target.files)}
                  multiple
                />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const FilterBlock = (
    displayId: string,
    title: string,
    filterComponentTypeObj: any,
    setUpdatedFilterObj: any,
    currentFilterState: number | string | [number, number] | string[],
    sideEffectFunction: ((newValue: number) => void) | null = null
  ) => {
    let filterActionComponent: any = null;

    if (filterComponentTypeObj.type === 'counter') {
      filterActionComponent = FilterCounter(
        filterComponentTypeObj.minCount,
        filterComponentTypeObj.maxCount,
        filterComponentTypeObj.specialLabels,
        currentFilterState as number,
        setUpdatedFilterObj,
        sideEffectFunction,
        filterComponentTypeObj.step
      );
    }

    if (filterComponentTypeObj.type === 'button') {
      filterActionComponent = FilterButtonGroup(
        filterComponentTypeObj.buttons,
        currentFilterState as string,
        setUpdatedFilterObj
      );
    }

    if (filterComponentTypeObj.type === 'slider') {
      filterActionComponent = FilterSlider(
        currentFilterState as [number, number],
        filterComponentTypeObj.rating_marks,
        setUpdatedFilterObj
      );
    }

    if (filterComponentTypeObj.type === 'multi_select_button') {
      filterActionComponent = MultipleSelectButtonGroup(
        filterComponentTypeObj.buttons,
        currentFilterState as string[],
        setUpdatedFilterObj,
        filterComponentTypeObj.maxSelect
      );
    }

    let filterTitleComponent: any = null;

    if (filterComponentTypeObj.type === 'multi_select_button') {
      filterTitleComponent = (
        <>
          {`${title}`}
          <br />
          {`(${(currentFilterState as string[]).length}/${
            filterComponentTypeObj.maxSelect
          }) Selected`}
        </>
      );
    } else {
      filterTitleComponent = title;
    }

    if (filterComponentTypeObj.type === 'text') {
      filterActionComponent = SimpleTextInput(
        filterComponentTypeObj.title,
        currentFilterState,
        setUpdatedFilterObj,
        filterComponentTypeObj.isNumber,
        filterComponentTypeObj.formatNumber,
        filterComponentTypeObj?.multiline,
        filterComponentTypeObj?.inputPrefix,
        filterComponentTypeObj?.maxMinNumberRange,
        filterComponentTypeObj?.endAdornmentComponent,
        filterComponentTypeObj?.maxLength
      );
    }

    if (filterComponentTypeObj.type === 'image') {
      filterActionComponent = ImageInput(
        filterComponentTypeObj.title,
        currentFilterState,
        setUpdatedFilterObj,
        filterComponentTypeObj.maxImageCount,
        filterComponentTypeObj?.maxSizeInMb
      );
    }

    const highlightStyle =
      highlitedFilterMode === displayId ? { background: COLOR_PRIMARY_1_100, py: '10px' } : {};

    return (
      <Grid
        container
        item
        direction="row"
        justifyContent={'space-between'}
        alignItems={'center'}
        xs={12}
      >
        <Grid container item xs={12} sm={4} sx={highlightStyle}>
          <Typography variant="body1" sx={styles.filterGroupHeadingTextColor}>
            {filterTitleComponent}
          </Typography>
        </Grid>
        <Grid
          container
          item
          xs={12}
          sm={8}
          justifyContent={{
            xs: 'flex-start',
            sm: 'flex-end',
          }}
          sx={{
            ...highlightStyle,
            [theme.breakpoints.down('sm')]: {
              mt: '10px !important',
            },
          }}
        >
          {filterActionComponent}
        </Grid>
      </Grid>
    );
  };

  const Filter = (filterJson: any) => {
    const filterComponents = filterJson.map((filter: any) => {
      return (
        <>
          {FilterBlock(
            filter.id,
            filter.title,
            filter.filterComponentTypeObj,
            filter.setUpdatedFilterObj,
            filter.currentFilterState,
            filter.sideEffectFunction
          )}

          {filter.dividerAfter && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
        </>
      );
    });

    return <>{filterComponents}</>;
  };

  const parseFilterJsonApi = () => {
    const propertySizeSqm =
      currentPropertySize != null && currentPropertySize !== 0
        ? propertySizeUnit === 'ft'
          ? feetToMeterConversion(currentPropertySize)
          : currentPropertySize
        : null;

    return buildReportFilterUpdatePayload({
      reportId: reportInputs.id,
      addressFull: reportInputs.address_full,
      addressShort: reportInputs.address_short,
      latitude: reportInputs.latitude,
      longitude: reportInputs.longitude,
      bedrooms: bedroom,
      bathrooms: bathroom,
      accommodates: accommodate,
      reportType: selectedReportType,
      minNights: stayDuration === 'both' ? null : stayDuration,
      professionallyManaged:
        professionallyManagedState === 'both' ? null : Number(professionallyManagedState),
      ratingMin: ratingState[0],
      ratingMax: ratingState[1],
      reviewCountMin: reviewCountState === 'any' ? null : Number(reviewCountState),
      amenities: amenitiesState.length === 0 ? null : amenitiesState,
      propertyPriceOrRent: updatedPropertyPriceOrRoomRent,
      propertyDetails: {
        reportName,
        propertySizeSqm,
        buildYear,
        linkToProperty,
        notes,
        propertyImageB64: images,
        existingPropertyImageUrl: reportInputs.property_image_urls,
      },
    });
  };

  const basicFilterJson: any = [
    {
      id: 'bedrooms',
      title: 'Bedrooms 🛏️',
      filterComponentTypeObj: {
        type: 'counter',
        defaultCounter: 2,
        minCount: 0,
        maxCount: 6,
        specialLabels: { 0: 'Studio', 6: '6+' },
      },
      currentFilterState: bedroom,
      setUpdatedFilterObj: setBedroom,
      dividerAfter: true,
      sideEffectFunction: handleUpdateBathroomAccommodateWithBedroomUpdate,
    },
    {
      id: 'bathrooms',
      title: 'Bathrooms 🚽',
      filterComponentTypeObj: {
        type: 'counter',
        defaultCounter: 1,
        minCount: 1,
        maxCount: 5,
        specialLabels: { 5: '5+' },
        step: 0.5,
      },
      currentFilterState: bathroom,
      setUpdatedFilterObj: setBathroom,
      dividerAfter: true,
      sideEffectFunction: null,
    },
    {
      id: 'accommodates',
      title: 'Accommodates 👥',
      filterComponentTypeObj: {
        type: 'counter',
        defaultCounter: 1,
        minCount: 1,
        maxCount: 16,
        specialLabels: { 16: '16+' },
      },
      currentFilterState: accommodate,
      setUpdatedFilterObj: setAccommodate,
      dividerAfter: true,
      sideEffectFunction: null,
    },
  ];

  const advancedFilterJson: any = [
    {
      id: 'stayDuration',
      title: 'Stay Length ⌛',
      filterComponentTypeObj: {
        type: 'button',
        buttons: { both: 'Both', 'short-stay': 'Short Stay', 'mid-stay': 'Mid Stay' },
      },
      currentFilterState: stayDuration,
      setUpdatedFilterObj: setStayDuration,
      dividerAfter: true,
      sideEffectFunction: null,
    },
    {
      id: 'professionallyManaged',
      title: 'Professionally Managed 🧑‍💼️',
      filterComponentTypeObj: {
        type: 'button',
        buttons: { 1: 'Yes', 0: 'No', both: 'Both' },
      },
      currentFilterState: professionallyManagedState,
      setUpdatedFilterObj: setProfessionallyManagedState,
      dividerAfter: true,
      sideEffectFunction: null,
    },
    {
      id: 'rating',
      title: 'Rating ⭐️',
      filterComponentTypeObj: {
        type: 'slider',
        rating_marks: rating_marks,
      },
      currentFilterState: ratingState,
      setUpdatedFilterObj: setRatingState,
      dividerAfter: true,
      sideEffectFunction: null,
    },
    {
      id: 'reviewCount',
      title: 'Number of Review 🧮',
      filterComponentTypeObj: {
        type: 'button',
        buttons: { any: 'Any', 1: '1+', 3: '3+', 10: '10+', 30: '30+' },
      },
      currentFilterState: reviewCountState,
      setUpdatedFilterObj: setReviewCountState,
      dividerAfter: true,
      sideEffectFunction: null,
    },
    {
      id: 'amenities',
      title: 'Amenities  🛍️',
      filterComponentTypeObj: {
        type: 'multi_select_button',
        buttons: {
          pool: 'Pool',
          hot_tub: 'Hot Tub',
          parking: 'Parking',
          pets: 'Pet Friendly',
          ac: 'AC',
          heating: 'Heating',
          breakfast: 'Breakfast',
          dryer: 'Dryer',
          washer: 'Washer',
          gym: 'Gym',
          kitchen: 'Kitchen',
          tv: 'TV',
          wifi: 'WiFi',
          bbq_grill: 'BBQ Grill',
          fireplace: 'Fireplace',
        },
        maxSelect: 3,
      },
      currentFilterState: amenitiesState,
      setUpdatedFilterObj: setAmenitiesState,
      dividerAfter: true,
      sideEffectFunction: null,
    },
  ];

  const handleOnFilterUpdateClick = () => {
    trackEditPerformed('input');
    const filterJsonObject = parseFilterJsonApi();
    handleReport('UPDATE', filterJsonObject);
  };

  const filterModal = () => {
    return (
      <Modal open={filterEditMode} onClose={handleCloseEditModal}>
        <Box sx={{ ...styles.filterModalBodyRoot }}>
          <IconButton onClick={handleCloseEditModal} sx={styles.filterModalCloseIcon}>
            <CloseIcon />
          </IconButton>

          <Grid container justifyContent="flex-start" xs={12}>
            <Typography variant="body1" sx={styles.filterHeading}>
              Default Filters
            </Typography>
          </Grid>

          <Grid container direction="column" spacing={2}>
            {Filter(basicFilterJson)}
          </Grid>

          {getCollapseHeadingButton(
            'Additional Filters',
            isAdvancedFilterOpen,
            setIsAdvancedFilterOpen
          )}

          <Collapse in={isAdvancedFilterOpen} timeout="auto" unmountOnExit>
            <Grid container direction="column" spacing={2} sx={{ position: 'relative' }}>
              {Filter(advancedFilterJson)}

              {!isUserSubscribed && (
                <DisabledBlurCard
                  subscriptionType="Expert"
                  onDisabledOptionClick={() => handleLoginOrPricingModalOpen('open')}
                  addBlur="1px"
                />
              )}
            </Grid>
          </Collapse>

          <Grid container direction="column" spacing={2}>
            <Grid container item xs justifyContent="flex-end" sx={{ mt: '20px' }}>
              <Button
                onClick={handleOnFilterUpdateClick}
                sx={{ ...styles.actionSectionButton, width: '200px' }}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    );
  };

  const subFilterTextList = getSubFilterText(isUserReportOwner);

  const handleKpiPercentileChange = event => {
    const value = event.target.value;
    if (value === 'comps' && !isCompsKpiPresent) {
      handleCompsUpdate();
    } else {
      setSelectedKpiPercentile(value as KpiPercentileType);
    }
  };

  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Grid
        item
        container
        alignItems="flex-start"
        justifyContent="center"
        spacing={2}
        xs={12}
        sx={{ ...styles.paper, ...styles.reportInputSection, height: matches ? 'auto' : '310px' }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: '+5',
          }}
        >
          <EditButton
            isDisabled={!isUserReportOwner}
            actionOnClick={() => {
              setFilterEditMode(true);
              trackEditOpened('input');
            }}
            isLocked={false}
          />
        </Box>
        <Grid item container xs={isMobile ? 12 : 8} alignItems="flex-start" justifyContent="center" spacing={2}>
          <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
            <Chip
              disabled={!isUserReportOwner}
              label={
                reportType === 'manage' ? (
                  reportType
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textTransform: 'capitalize',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ marginRight: '10px' }}>
                      {reportType == 'rent' ? 'Monthly Rent' : reportType}{' '}
                      {exchangeRates[currency].symbol}
                      {formatCurrency(String(updatedPropertyPriceOrRoomRent))}
                    </span>
                    {currentPropertySize && (
                      <span style={{ textTransform: 'lowercase', marginRight: '10px' }}>
                        {formatCurrency(String(roundFloat(currentPropertySize)))} {propertySizeUnit}
                        <sup style={{ verticalAlign: 'super', fontSize: '10px' }}>2</sup>{' '}
                      </span>
                    )}
                    {buildYear && <span>Year {buildYear}</span>}
                  </Typography>
                )
              }
              color="primary"
              variant="outlined"
              onDelete={() => {}}
              deleteIcon={
                <ModeEditOutlineOutlinedIcon sx={{ fontSize: '18px', pointerEvents: 'none' }} />
              }
              onClick={() => {
                if (isOnboardingComplete && !isUserSubscribed) {
                  handleLoginOrPricingModalOpen('open', 'payment');
                  return;
                }
                isUserReportOwner && setReportTypeEditMode(true);
                trackEditOpened('input');
              }}
              sx={{
                ...styles.reportInputSectionChipStyle(COLOR_PRIMARY, isUserReportOwner),
                height: 'auto',
                py: 1,
                borderRadius: '900px',
              }}
            />
          </Grid>
          {reportName && (
            <Grid item xs={12} justifyContent={'flex-start'}>
              <Typography
                variant="body1"
                sx={{ fontSize: '20px', fontWeight: 500, color: COLOR_PRIMARY }}
              >
                {reportName}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} justifyContent={'flex-start'}>
            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile ? '14px' : '20px',
                fontWeight: 500,
                ...(isMobile ? {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                } : {}),
              }}
            >
              {isMobile ? (
                <TooltipNew title={addressFull} content={''}>
                  <span>{addressFull}</span>
                </TooltipNew>
              ) : (
                addressFull
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} justifyContent={'flex-start'}>
            <Stack direction="row" spacing={0.5} flexWrap={'wrap'}>
              <Chip
                label={`${bedrooms} 🛏️`}
                variant="outlined"
                sx={styles.reportInputSectionChipStyle('#000', isUserReportOwner)}
                onClick={() => isUserReportOwner && handleUnitFilterButtonClick('bedrooms')}
              />
              <Chip
                label={`${bathrooms} 🚽`}
                variant="outlined"
                sx={styles.reportInputSectionChipStyle('#000', isUserReportOwner)}
                onClick={() => isUserReportOwner && handleUnitFilterButtonClick('bathrooms')}
              />
              <Chip
                label={`${accommodates} 👥`}
                variant="outlined"
                sx={styles.reportInputSectionChipStyle('#000', isUserReportOwner)}
                onClick={() => isUserReportOwner && handleUnitFilterButtonClick('accommodates')}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} justifyContent={'flex-start'} sx={{ mt: '10px' }}>
            <Grid
              container
              item
              xs={12}
              justifyContent={'flex-start'}
              alignItems={'center'}
              gap={1}
            >
              <FormControl>
                <Select
                  value={selectedKpiPercentile}
                  label=""
                  onChange={handleKpiPercentileChange}
                  sx={{
                    '& .MuiSelect-select': {
                      padding: '4px 0px 4px 10px',
                    },
                  }}
                >
                  <MenuItem value={'25'}>Underperforming (25th)</MenuItem>
                  <MenuItem value={'50'}>Median (50th)</MenuItem>
                  <MenuItem value={'75'}>Top Performing (75th)</MenuItem>
                  <MenuItem value={'comps'}>
                    Reference Comps ({selectedCompsCount} selected)
                  </MenuItem>
                  {isEditedKpiAvailable && <MenuItem value={'edited'}>Custom</MenuItem>}
                </Select>
              </FormControl>
              <InfoButton
                tooltipContent="Estimate annual revenue in different ways: Underperforming (lower end), Median (middle), Top Performing (higher end), or Reference Comps (based on listings you select from the map)"
                iconColor={COLOR_TEXT_4}
              />
            </Grid>
          </Grid>

          {!isMobile && (
            <Grid item xs={12} justifyContent={'flex-start'}>
              <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 400, display: 'flex' }}>
                {subFilterTextList.map((subFilterText, index) => (
                  <>
                    {subFilterText}
                    <span style={{ marginRight: '10px' }}>
                      {subFilterTextList.length !== index + 1 ? ',' : ''}
                    </span>
                  </>
                ))}
              </Typography>
            </Grid>
          )}
        </Grid>
        {!isMobile && (
        <Grid
          item
          container
          xs={4}
          alignItems="flex-start"
          justifyContent="center"
          spacing={2}
          sx={{ position: 'relative' }}
        >
          {reportInputs.property_image_urls && (
            <>
              <Box
                sx={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover',
                  marginTop: '40px',
                  borderRadius: '4px',
                  background: `url('${reportInputs.property_image_urls}')`,
                }}
              >
                <img
                  src={
                    reportInputs.property_image_urls
                      ? encodeURI(reportInputs.property_image_urls)
                      : undefined
                  }
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    backdropFilter: 'blur(20px)',
                  }}
                />
              </Box>
              {reportInputs.link_to_property && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    background: '#fff',
                    padding: '5px',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px -4px #0003',
                    cursor: 'pointer',
                    transition: 'background 0.25s ease',
                    '&:hover': {
                      background: '#aba',
                    },
                  }}
                  onClick={() => window.open(reportInputs.link_to_property)}
                >
                  <OpenInNewIcon />
                </Box>
              )}
            </>
          )}
        </Grid>
        )}
        {!isMobile ? (
          <Grid item container xs={12} justifyContent={'flex-start'} alignItems={'center'}>
            <Grid item xs={6}>
              {notes && (
                <Grid item xs={12} justifyContent={'flex-start'}>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: '16px', fontWeight: 400, display: 'flex' }}
                  >
                    <span style={{ marginRight: '5px' }}>Notes: </span>
                    <span style={{ color: COLOR_GREY_600 }}>{notes}</span>
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: COLOR_GREY_600, textAlign: 'right' }}>
                Created on: {beautifyDate(createdAt)}
              </Typography>
            </Grid>
          </Grid>
        ) : null}
      </Grid>

      {reportTypeEditModal()}
      {filterModal()}
    </>
  );
};

export default RepotPageInputSection;
